import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import RestrictedCommoditiesValidator from "../validators/RestrictedCommoditiesValidator";

import { useRestrictedCommodities, useAddRestrictedCommodity, useUpdateRestrictedCommodity, useDeleteRestrictedCommodity } from "../../../services/settings";
import {
  MaskedText, MaskedNumber, LightTooltip, DatePicker
} from "../../../controls";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import CountryLookup from "./CountryLookup";
import CountryNameTooltip from "./CountryNameTooltip";


const dateFormat = "DD/MM/YYYY";

const RestrictedCommoditiesList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(RestrictedCommoditiesValidator, true);

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddUnitOfMeasure = useAddRestrictedCommodity();
  const handleUpdateUnitOfMeasure = useUpdateRestrictedCommodity();
  const handleDeleteUnitOfMeasure = useDeleteRestrictedCommodity();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const unitsOfMeasure = useRestrictedCommodities({tableRef, queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9]-",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...ColumnWidth(80, undefined, undefined, {paddingLeft:12}, {paddingLeft:12}),
        title: "Country Code",
        field: "countryCode",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <CountryLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip
            title={<CountryNameTooltip countryCode={rowData.countryCode} departureTime={rowData.departureTime}/>}
          >
            <div style={{width: 250, maxWidth:250, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
              {rowData.countryCode}
            </div>
          </LightTooltip>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(400, undefined, undefined, {paddingLeft:12}, {paddingLeft:12}),
        title: "Commodity",
        field: "commodityName",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(150)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(undefined, undefined, undefined, {paddingLeft:12}, {paddingLeft:12}),
        title: "Sanction",
        field: "sanction",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(150)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, {paddingLeft:12}, {paddingLeft:12}),
        title: "Valid From",
        field: "validFrom",
        type: "date",
        // emptyValue: today,
        // initialEditValue:today,
        editComponent: (props) => (
          <DatePicker
            //minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, {paddingLeft:12}, {paddingLeft:12}),
        title: "Valid To",
        field: "validTo",
        type: "date",
        // emptyValue: today,
        // initialEditValue:today,
        editComponent: (props) => (
          <DatePicker
            //              minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
    ];
  };

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Restricted Commodity"
            columns={getColumns({canEdit: state.canEdit})}
            data={unitsOfMeasure.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddUnitOfMeasure.mutate({record: newData, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateUnitOfMeasure.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteUnitOfMeasure.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default RestrictedCommoditiesList;
